import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, BookOpen, CalendarDays, ScrollText } from "lucide-react";
import { getPages, createPage, updatePage, deletePage, apiErr } from "../lib/api";
import { PageHead, Stat, Modal, Empty, Pill, BtnPrimary, BtnGhost, Field, inputCls, areaCls } from "./_ui";
import { BLOG_POSTS } from "@/data/blog";
import { STOTRAS } from "@/data/stotras";
import { FESTIVALS } from "@/data/festivals";

const EMPTY = { title: "", slug: "", excerpt: "", body: "", status: "draft" };
const kebab = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function PageForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    const body = { title: f.title.trim(), excerpt: f.excerpt, body: f.body, status: f.status };
    setBusy(true);
    try {
      if (f.id) await updatePage(f.id, body);
      else await createPage({ ...body, slug: f.slug.trim() || kebab(f.title) });
      toast.success(f.id ? "Page updated" : "Page created");
      onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title={f.id ? "Edit page" : "New content page"} onClose={onClose} testid="page-form-modal"
      footer={<><BtnGhost onClick={onClose}>Cancel</BtnGhost><BtnPrimary onClick={save} disabled={busy} data-testid="page-form-save">{busy ? "Saving…" : "Save"}</BtnPrimary></>}>
      <div className="grid grid-cols-1 gap-4">
        <Field label="Title"><input value={f.title} onChange={set("title")} data-testid="page-field-title" className={inputCls} /></Field>
        {!f.id && <Field label="Slug (auto if blank)"><input value={f.slug} onChange={set("slug")} placeholder={kebab(f.title)} className={inputCls} /></Field>}
        <Field label="Excerpt"><input value={f.excerpt} onChange={set("excerpt")} className={inputCls} /></Field>
        <Field label="Body"><textarea rows={6} value={f.body} onChange={set("body")} className={areaCls} /></Field>
        <Field label="Status"><select value={f.status} onChange={set("status")} data-testid="page-field-status" className={inputCls}><option value="draft">Draft</option><option value="published">Published</option></select></Field>
      </div>
    </Modal>
  );
}

function LibCard({ icon: Icon, label, count, tint }) {
  return (
    <div className="rounded-2xl border border-[#E9E2D6] bg-white p-5">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: tint.bg }}><Icon size={17} style={{ color: tint.fg }} /></span>
      <p className="mt-3 font-heading text-3xl font-semibold text-[#2C241B]">{count}</p>
      <p className="font-body text-sm text-[#8A8072]">{label}</p>
    </div>
  );
}

export default function Content() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);

  const load = () => getPages().then(setRows).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(apiErr(e)); } };

  const published = rows.filter((r) => r.status === "published").length;

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-content">
      <PageHead eyebrow="Editorial" title="Content"
        desc="Create custom marketing pages and see the full library of blogs, stotras and festival guides already live on the site."
        action={<BtnPrimary onClick={() => setForm(EMPTY)} data-testid="page-add-btn" className="inline-flex items-center gap-1.5"><Plus size={16} /> New page</BtnPrimary>} />

      <h2 className="mt-7 font-heading text-lg font-semibold text-[#2C241B]">Published content library</h2>
      <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <LibCard icon={FileText} label="Blog posts" count={BLOG_POSTS.length} tint={{ bg: "#FBEDE7", fg: "#D84315" }} />
        <LibCard icon={ScrollText} label="Stotras" count={STOTRAS.length} tint={{ bg: "#F0ECF7", fg: "#6A4EA8" }} />
        <LibCard icon={CalendarDays} label="Festival guides" count={FESTIVALS.length} tint={{ bg: "#EEF2E6", fg: "#6D8B44" }} />
        <LibCard icon={BookOpen} label="Custom pages" count={rows.length} tint={{ bg: "#E6F0EE", fg: "#2F7A6B" }} />
      </div>

      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold text-[#2C241B]">Custom pages</h2>
        <span className="font-body text-sm text-[#8A8072]">{published} published · {rows.length - published} draft</span>
      </div>
      <div className="mt-3 space-y-2">
        {rows.map((p) => (
          <div key={p.id} data-testid={`page-row-${p.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#E9E2D6] bg-white p-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2"><span className="font-heading text-base font-semibold text-[#2C241B]">{p.title}</span><Pill status={p.status} /></div>
              <p className="mt-0.5 font-body text-xs text-[#A79C8A]">/{p.slug}{p.excerpt ? ` · ${p.excerpt}` : ""}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => setForm({ ...EMPTY, ...p })} data-testid={`page-edit-${p.id}`} className="inline-flex items-center gap-1 rounded-lg border border-[#E5DFD3] px-2.5 py-1.5 font-body text-xs font-medium text-[#5C5449] hover:bg-[#F5F1E8]"><Pencil size={13} /> Edit</button>
              <button onClick={() => act(() => updatePage(p.id, { status: p.status === "published" ? "draft" : "published" }), p.status === "published" ? "Unpublished" : "Published")} className="rounded-lg border border-[#E5DFD3] px-2.5 py-1.5 font-body text-xs font-medium text-[#5C5449] hover:bg-[#F5F1E8]">{p.status === "published" ? "Unpublish" : "Publish"}</button>
              <button onClick={() => { if (window.confirm(`Delete ${p.title}?`)) act(() => deletePage(p.id), "Page deleted"); }} data-testid={`page-delete-${p.id}`} className="inline-flex items-center gap-1 rounded-lg bg-[#FDECEA] px-2.5 py-1.5 font-body text-xs font-semibold text-[#C62828] hover:bg-[#f8dcd8]"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {!loading && rows.length === 0 && <Empty>No custom pages yet — create one to start building landing content.</Empty>}
      </div>

      {form && <PageForm initial={form} onClose={() => setForm(null)} onSaved={load} />}
    </div>
  );
}
